import type { PendingAction, QuickMeetingUnavailableReason, RecordingStatus } from './types';

// Every user-facing string of the Quick Meeting chrome. The notice is the draft from
// QUICK-MEETING-PLAN §2.2 / §6.2, versioned with QM_LIMITS.NOTICE_VERSION.

export const QM_COPY = {
  micLabel: 'Quick meeting',
  popoverTitle: 'Quick meeting',
  titleLabel: 'Title (optional)',
  titlePlaceholder: 'Quick meeting',
  notice:
    'Recording audio for a transcript and minutes. Clara sends the audio to Sarvam AI to process it. ' +
    'Only you can listen in Clarix. You can download or share the notes; copies you download or send ' +
    "can't be deleted by Clarix. Tell everyone present before you start.",
  noticeDetailsToggle: 'How recordings are handled',
  noticeDetails: [
    'Purpose: a transcript, key points and draft minutes of this meeting, for you to review.',
    'Processor: Sarvam AI transcribes the audio and drafts the minutes for Clarix.',
    'Access: only you can play or download the audio. You can share the minutes, or the minutes and transcript, with workspace members you choose.',
    'Retention: the audio, transcript and AI draft are deleted automatically after 90 days. Minutes you save stay with the meeting.',
    'Deletion: you can delete the audio, or everything, at any time from the recording page.',
  ],
  start: 'Start recording',
  starting: 'Starting…',
  askingMic: 'Allow microphone access in your browser to start.',
  micPickerLabel: 'Microphone',
  systemDefault: 'System default',
  systemDefaultOf: (label: string) => `System default (${label})`,
  savedMic: 'Your saved microphone',
  unnamedMic: 'Microphone',
  micNotConnected: (label: string) => `${label} (not connected)`,
  micFellBack: (saved: string, now: string) => `${saved} not found — using ${now}`,
  micSwitchedTo: (label: string) => `Switched to ${label}`,
  micDisconnected: (label: string) => `Mic disconnected, now using ${label}`,
  micSwitchFallback: (label: string) => `Couldn't switch — now using ${label}`,
  micCantSwitchHere: (label: string) => `Can't switch to ${label} in this browser`,
  micNewDetected: (label: string) => `New mic detected: ${label}`,
  micDefaultChanged: (label: string) => `Your system mic is now ${label}`,
  micSwitchQuestion: 'Switch the recording to it?',
  micSwitch: 'Switch',
  undo: 'Undo',
  micInputLost: 'No microphone. Connect one or the recording stops.',
  micInputLostShort: 'No microphone',
  quietTitle: 'No speech heard for a while',
  quietBody: 'If someone is talking, this mic may not be picking them up. You can pick another one here.',
  quietShort: 'No speech heard',
  changeMic: 'Change microphone',
  micDeniedTitle: 'Microphone blocked',
  tryAgain: 'Try again',
  unavailable: {
    NO_PERMISSION: "Your role doesn't allow recording quick meetings.",
    STORAGE_NOT_CONFIGURED: "Recording isn't set up for this workspace yet.",
    QUOTA_EXCEEDED: "You've used today's recording minutes. Try again tomorrow.",
  } satisfies Record<QuickMeetingUnavailableReason, string>,
  micUnavailable: 'unavailable',
  minutesLeft: (minutes: number) => `${minutes} min of recording left today`,
  pause: 'Pause',
  resume: 'Resume',
  stop: 'Stop',
  paused: 'Paused',
  saving: 'Saving…',
  saveFailed: "Couldn't save",
  retry: 'Retry',
  recordingLabel: 'Recording',
  anotherTab: 'Recording in another tab',
  stopRemote: 'Stop',
  autoStopSoonTitle: 'Auto-stops in 10 min',
  autoStopSoonBody: 'Quick meetings stop at 115 minutes, pauses included. Start a new one to keep going.',
  autoStopped: 'Recording stopped at the 115-minute limit.',
  tooShort: 'Too short to transcribe. The recording was discarded.',
  endedElsewhere: 'This recording was ended from another tab or device.',
  savedTitle: 'Recording saved',
  savedBody: 'Clara is preparing the transcript and minutes.',
  noAudio: 'No audio was recorded.',
  /** A take no tab drives any more (closed, crashed, lost signal): nothing is cleared on its own. */
  pausedRow: 'Paused — save or discard it.',
  orphanBlocksStart: 'Save or discard your paused recording first.',
  slowProcessing: "Taking longer than usual — we'll retry automatically",
  asleepPaused: 'Recording paused while your computer was asleep.',
  stopAndSave: 'Stop & save',
  offlineRecording: "Offline — still recording. We'll upload when you're back online.",
  offlineSaving: "Offline — we'll upload when you're back online.",
  offline: 'Offline',
  cantSave: "This recording can't be saved anymore.",
  audioNotReceived: "The audio didn't reach the server. It's still on this device.",
  savedElsewhere: 'This recording was saved from another device.',
  stillRunning: 'This recording is still running.',
  offlineTryAgain: "You're offline. Try again when you're back online.",
  uploadStuck: "Couldn't upload. Try again or download from this device.",
  partialSaved: 'Saved what reached the server. The full audio is still on this device.',
  download: 'Download',
  downloadFromDevice: 'Download from this device',
  nothingOnDevice: 'No audio is left on this device.',
  onThisDevice: 'On this device',
  keptOnDevice: 'Audio kept on this device',
  deleteFromDevice: 'Delete',
  deleteLocalTitle: 'Delete this audio from this device?',
  deleteLocalBody: "It can't be recovered afterwards. Download it first if you need it.",
  deleteLocalConfirm: 'Delete',
  saveFailedBody: 'The audio is still on this device. Retry to upload it.',
  readyTitle: 'Notes ready',
  failedTitle: "Couldn't process the recording",
  noSpeechTitle: "We couldn't hear clear speech in this recording.",
  open: 'Open',
  openRecording: 'Open recording',
  writeMomManually: 'Write MoM manually',
  retryProcessing: 'Retry processing',
  process: 'Process',
  save: 'Save',
  discard: 'Discard',
  allRecordings: 'All recordings',
  yourRecordings: 'Your recordings',
  inAnotherTabRow: 'Recording in another tab',
  discardTitle: 'Discard this recording?',
  discardBody: 'The audio and its meeting are deleted. This cannot be undone.',
  discardConfirm: 'Discard',
  actionFailed: 'That didn’t work. Try again.',
  signOut: {
    title: 'Your recording isn’t saved yet',
    body: 'The last part of the audio is still on this device and could not be uploaded.',
    footnote: 'Deleting it here keeps what was already uploaded; you can save or discard that later.',
    upload: 'Upload now',
    uploading: 'Uploading…',
    deleteLocal: 'Delete from this device',
    back: 'Back',
  },
  floatingLabel: 'Quick meeting recording',
} as const;

/** Who can fix it, where the reason is a workspace setting rather than the user's own limit. */
export const UNAVAILABLE_HINT: Partial<Record<QuickMeetingUnavailableReason, string>> = {
  STORAGE_NOT_CONFIGURED: 'Workspace admins: ask Clarix support to set up recording storage for this workspace.',
};

export const STATUS_COPY: Partial<Record<RecordingStatus, string>> = {
  normalizing: 'Preparing audio',
  queued: 'Queued',
  transcribing: 'Transcribing',
  summarizing: 'Writing minutes',
  ready: 'Notes ready',
  no_speech: "We couldn't hear clear speech in this recording.",
  failed: 'Processing failed',
  upload_failed: 'Upload interrupted',
  stale: QM_COPY.pausedRow,
  salvaged_pending_review: 'Saved from an unfinished recording: process or discard',
  recording: 'Recording',
};

/** `failure.code` → reason (§18.9 "Failure codes"). */
export const FAILURE_COPY: Record<string, string> = {
  AUDIO_UNREADABLE: "The audio couldn't be read.",
  AUDIO_TOO_LONG: 'The recording is longer than the 2-hour processing limit.',
  RECORDING_TOO_LARGE: 'The recording is too large to process.',
  NORMALIZE_FAILED: "Couldn't prepare the audio.",
  AI_UNAVAILABLE: 'The AI service was unavailable.',
  STT_FAILED: 'Transcription failed.',
  STT_EMPTY: "We couldn't hear clear speech. If you spoke, tap Retry.",
  STT_TIMEOUT: 'Transcription took too long.',
  PROVIDER_QUOTA: "The AI provider's limit was reached.",
  LLM_FAILED: "Couldn't write the minutes. The transcript is ready.",
  LLM_INVALID_OUTPUT: "Couldn't write the minutes. The transcript is ready.",
};

/** `POST /quick-meetings` and recording-write codes (§18.3 "Errors"). */
export const START_ERROR_COPY: Record<string, string> = {
  RECORDING_IN_PROGRESS: "You're already recording in another tab or device.",
  RECORDING_QUOTA_EXCEEDED: "You've reached the recording limit.",
  STORAGE_NOT_CONFIGURED: QM_COPY.unavailable.STORAGE_NOT_CONFIGURED,
  NOTICE_NOT_ATTESTED: 'The recording notice changed. Review it and start again.',
  API_TOKEN_NOT_ALLOWED: "Recordings can't be started with an API token.",
  FORBIDDEN: "Your role doesn't allow recording quick meetings.",
  RECORDING_TOO_LARGE: 'The recording is too large to process.',
  RECORDING_NOT_FOUND: 'This recording no longer exists.',
  RECORDING_PARTS_MISMATCH: "Some of the audio didn't reach the server.",
  RECORDING_DELETE_UNAVAILABLE: "This recording can't be deleted right now.",
};

/** `RECORDING_QUOTA_EXCEEDED` → `details.scope`. */
export const QUOTA_COPY: Record<string, string> = {
  user_daily: "You've used today's recording minutes. Try again tomorrow.",
  workspace_monthly: "Your workspace has used this month's recording minutes.",
};

export const START_FALLBACK = "Couldn't start the recording. Try again.";
export const ENGINE_START_FALLBACK = "Couldn't start the microphone. Try again.";
export const ANOTHER_TAB_START = "You're already recording in another tab.";
export const MIC_UNSUPPORTED = "This browser can't record audio here.";
export const MIC_NO_DEVICE = 'No microphone was found. Connect one and try again.';

/** Browser-specific steps to re-enable a blocked microphone (§2.2 "denied"). */
export const MIC_DENIED_STEPS: Record<'chromium' | 'safari' | 'firefox' | 'other', string> = {
  chromium: 'Click the site settings icon left of the address, set Microphone to Allow, then try again.',
  safari: 'Open Safari › Settings for This Website, set Microphone to Allow, then try again.',
  firefox: 'Click the microphone icon in the address bar, remove the block, then try again.',
  other: "Allow microphone access in your browser's site settings, then try again.",
};

const meetings = (n: number) => `${n} meeting${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''}`;
const peopleCount = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

export const CHIP_COPY = {
  chipLabel: 'Next meeting',
  liveNow: 'Live now',
  /** The badge on a live meeting: the time since its start. */
  liveFor: (elapsed: string) => `Live · ${elapsed}`,
  startingNow: 'Starting now',
  upNext: 'Up next',
  hours: (h: number) => `${h} h`,
  minutes: (m: number) => `${m} min`,
  in: (span: string) => `in ${span}`,
  left: (span: string) => `${span} left`,
  endingNow: 'Ending now',
  startsIn: (countdown: string) => `Starts in ${countdown}`,
  joinOpens: (time: string) => `Join opens ${time}`,
  today: 'Today',
  tomorrow: 'Tomorrow',
  privateMeeting: 'Private meeting',
  quickMeeting: 'Quick meeting',
  meeting: 'Meeting',
  room: 'Room',
  location: 'Location',
  /** A room deleted since the booking keeps its name, marked so nobody heads for it. */
  roomRemoved: (label: string) => `${label} (removed)`,
  free: 'You’re free for the next 8 days',
  allClear: 'All clear',
  join: 'Join',
  joinMeeting: 'Join meeting',
  open: 'Open',
  openInCalendar: 'Open in Calendar',
  openCalendar: 'Open Calendar',
  laterToday: 'Later today',
  /** `conference.app` → its product name; 'none' and anything unknown have no row. */
  conferenceApp: { teams: 'Microsoft Teams', meet: 'Google Meet', zoom: 'Zoom' } as Readonly<Record<string, string>>,
  people: peopleCount,
  morePeople: (n: number) => `+${n}`,
  names: (first: string[], more: number) =>
    more > 0
      ? `${first.join(', ')} and ${more} more`
      : first.length > 1
        ? `${first.slice(0, -1).join(', ')} and ${first[first.length - 1]}`
        : (first[0] ?? ''),
  meetingProgress: 'Time into the meeting',
  startProgress: 'Time until the meeting starts',
  needYou: (n: number) => `${meetings(n)} you`,
  needsSummary: {
    mom: 'Minutes to write',
    allocate: 'Hours to log',
    both: 'Minutes to write and hours to log',
  } satisfies Record<PendingAction | 'both', string>,
  waitingCount: (n: number) => `${n} waiting`,
  waitingOnYou: 'Waiting on you',
  waitingBody: 'Meetings that ended and still need minutes or hours.',
  action: { allocate: 'Log hours', mom: 'MoM due' } satisfies Record<PendingAction, string>,
  actionDue: { allocate: 'Hours to log', mom: 'Minutes due' } satisfies Record<PendingAction, string>,
  actionVerb: { allocate: 'Log', mom: 'Write' } satisfies Record<PendingAction, string>,
  actionFor: {
    allocate: (title: string) => `Log hours for ${title}`,
    mom: (title: string) => `Write minutes for ${title}`,
  } satisfies Record<PendingAction, (title: string) => string>,
  /** The card lists the oldest few; the rest of the server's count is named, not hidden. */
  moreWaiting: (n: number) => `and ${n} more waiting on you`,
  seeAll: (n: number) => `See all ${n} in Calendar`,
  extend: 'Extend',
  extendOption: (minutes: number, until: string) => `+${minutes} min · until ${until}`,
  extendLoading: 'Checking the room…',
  extendLoadFailed: 'Couldn’t check the times. Try again',
  roomBooked: 'Room booked',
  roomBookedBy: (title: string, at: string) => `Room booked · ${title} at ${at}`,
  peopleBusy: (n: number) => `${peopleCount(n)} busy`,
  extendedTo: (time: string) => `Extended to ${time}`,
  endMeeting: 'End meeting',
  endTitle: 'End this meeting for everyone?',
  endBody: 'It moves to time allocation now, and the room frees up.',
  keepGoing: 'Keep going',
  ending: 'Ending…',
  meetingEnded: 'Meeting ended',
  /** §11.1 error codes of `end`, `extend` and `extend-options`. */
  liveActionError: {
    ROOM_UNAVAILABLE: 'The room is booked then. Pick a shorter extension.',
    VERSION_CONFLICT: 'This meeting just changed. Check it and try again.',
    INVALID_STATE: 'This meeting already ended.',
    FORBIDDEN: 'Only the host or a co-host can change this meeting.',
    NOT_FOUND: 'This meeting no longer exists.',
  } as Readonly<Record<string, string>>,
  liveActionFailed: 'That didn’t work. Try again.',
} as const;

/** The top bar's quick Schedule meeting / Book a room popovers. */
export const QUICK_CREATE_COPY = {
  meeting: {
    title: 'Schedule meeting',
    submit: 'Schedule',
    done: 'Meeting scheduled',
  },
  room: {
    title: 'Book a room',
    submit: 'Book room',
    done: 'Room booked',
    /** The event's title; Calendar requires one and the person can rename it there. */
    eventTitle: 'Room booking',
  },
  namePlaceholder: 'Meeting name',
  nameLabel: 'Meeting name',
  dateLabel: 'Date',
  startLabel: 'Start time',
  durationLabel: 'Duration',
  roomsLabel: 'Free rooms',
  moreOptions: 'More options',
  open: 'Open',
  findingRooms: 'Finding free rooms…',
  noRooms: 'No room is free at this time. Try another time.',
  roomsFailed: "Couldn't load rooms. Try again.",
  seats: (n: number) => `${n} ${n === 1 ? 'seat' : 'seats'}`,
  peopleLabel: 'People',
  /** Shown under the field on Schedule / Book room. */
  required: {
    name: 'Add a meeting name.',
    room: 'Pick a room.',
  },
  pastTime: 'That time has passed. Pick a later time.',
  roomTaken: 'That room was just booked. Pick another one.',
  roomRemoved: 'That room was removed. Pick another one.',
  forbidden: "Your role doesn't allow this.",
  offline: "You're offline. Check your connection and try again.",
  noCalendar: "Your calendar isn't ready yet. Open Calendar once, then try again.",
  /** Only when the server gives no reason of its own. */
  failed: "Couldn't save. Try again.",
} as const;

